import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { neutralSkeletonState } from '../lib/seed';
import { migrate } from '../lib/io';
import { deriveAll, currentDate } from '../lib/derive';
import { deriveGateAccountability } from '../lib/gateAccountability';
import { api, routeForPath, ApiError, clearSession } from './api';

/**
 * API-backed store.
 *
 * Reads happen once on boot (GET /api/v1/state) and are held in memory so the
 * derivation layer and every page stay synchronous. Writes are optimistic —
 * the UI updates immediately, then the change is queued to the API in order.
 * If a write fails the error surfaces in the top bar rather than being
 * swallowed, because silently losing a founder's evidence record is worse than
 * an ugly banner.
 */

const Ctx = createContext(null);

export function uid(prefix = 'x') {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

function setPath(obj, path, value) {
  const parts = Array.isArray(path) ? path : String(path).split('.');
  if (!parts.length) return value;
  const [head, ...rest] = parts;
  const base = Array.isArray(obj) ? [...obj] : { ...(obj || {}) };
  base[head] = rest.length ? setPath(base[head], rest, value) : value;
  return base;
}

export function getPath(obj, path, fallback) {
  const parts = Array.isArray(path) ? path : String(path).split('.');
  let cur = obj;
  for (const p of parts) {
    if (cur === null || cur === undefined) return fallback;
    cur = cur[p];
  }
  return cur === undefined ? fallback : cur;
}

export function StoreProvider({ children }) {
  const [state, setState] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(0);
  const queue = useRef(Promise.resolve());

  const hydrate = useCallback(async () => {
    setStatus('loading');
    setError(null);
    try {
      const snapshot = await api.getState();
      setState(migrate(snapshot ?? {}));
      setStatus('ready');
    } catch (err) {
      if (err?.status === 401) {
        clearSession();
        window.location.reload();
        return;
      }
      setError(err);
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  /** Serialises writes so two edits to the same record cannot race. */
  const enqueue = useCallback((fn) => {
    setPending((n) => n + 1);
    queue.current = queue.current
      .then(fn)
      .catch((err) => {
        setError(err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err?.message ?? err)));
      })
      .finally(() => setPending((n) => Math.max(0, n - 1)));
    return queue.current;
  }, []);

  const api_ = useMemo(() => {
    const local = (fn) => setState((prev) => (prev ? fn(prev) : prev));

    const set = (path, value) => {
      local((s) => setPath(s, path, value));
      const route = routeForPath(String(path));
      if (route.kind === 'collection') {
        return enqueue(() => api.replaceCollection(route.resource, value ?? []));
      }
      if (route.kind === 'document') {
        return enqueue(() =>
          route.inner
            ? api.patchDocument(route.name, route.inner, value)
            : api.putDocument(route.name, value),
        );
      }
      console.warn(`Bridge45: ${route.reason} — change kept in memory only.`);
      return Promise.resolve();
    };

    const add = (path, item) => {
      let nextList = [];
      local((s) => {
        nextList = [...(getPath(s, path, []) || []), item];
        return setPath(s, path, nextList);
      });
      const route = routeForPath(String(path));
      if (route.kind === 'collection') {
        return enqueue(() => api.createRecord(route.resource, item));
      }
      // Small append-only lists that live inside a document — the D001 stage,
      // constraint and primary-designation histories. Write the whole array.
      if (route.kind === 'document' && route.inner) {
        return enqueue(() => api.patchDocument(route.name, route.inner, nextList));
      }
      console.warn(`Bridge45: cannot create inside "${path}".`);
      return Promise.resolve();
    };

    const patch = (path, id, changes) => {
      let nextList = [];
      let merged = null;
      local((s) => {
        nextList = (getPath(s, path, []) || []).map((x) => {
          if (x.id !== id) return x;
          merged = { ...x, ...changes };
          return merged;
        });
        return setPath(s, path, nextList);
      });
      const route = routeForPath(String(path));
      if (route.kind === 'collection') {
        // Client migrate may invent seed rows (e.g. work packages) that never
        // existed on the API. PATCH 404 → CREATE with the merged local row.
        return enqueue(() =>
          api.updateRecord(route.resource, id, changes).catch((err) => {
            if (err instanceof ApiError && err.status === 404 && merged) {
              return api.createRecord(route.resource, merged);
            }
            throw err;
          }),
        );
      }
      if (route.kind === 'document' && route.inner) {
        return enqueue(() => api.patchDocument(route.name, route.inner, nextList));
      }
      return Promise.resolve();
    };

    const patchBy = (path, matchFn, changes) => {
      let touched = [];
      local((s) => {
        const list = getPath(s, path, []) || [];
        touched = list.filter(matchFn).map((x) => x.id);
        return setPath(s, path, list.map((x) => (matchFn(x) ? { ...x, ...changes } : x)));
      });
      const route = routeForPath(String(path));
      if (route.kind !== 'collection') return Promise.resolve();
      return enqueue(async () => {
        for (const id of touched) await api.updateRecord(route.resource, id, changes);
      });
    };

    const remove = (path, id) => {
      let nextList = [];
      local((s) => {
        nextList = (getPath(s, path, []) || []).filter((x) => x.id !== id);
        return setPath(s, path, nextList);
      });
      const route = routeForPath(String(path));
      if (route.kind === 'collection') {
        return enqueue(() => api.deleteRecord(route.resource, id));
      }
      if (route.kind === 'document' && route.inner) {
        return enqueue(() => api.patchDocument(route.name, route.inner, nextList));
      }
      return Promise.resolve();
    };

    /** Full snapshot replace — JSON import and reset both land here. */
    const replaceAll = (next) => {
      const shaped = migrate(next);
      setState(shaped);
      return enqueue(async () => {
        await api.importState(shaped);
        const fresh = await api.getState();
        setState(migrate(fresh ?? {}));
      });
    };

    const reset = () => replaceAll(neutralSkeletonState());

    const apply = (fn) => local(fn);

    return { set, add, patch, patchBy, remove, replaceAll, reset, apply, reload: hydrate };
  }, [enqueue, hydrate]);

  const derived = useMemo(() => {
    if (!state) return null;
    const d = deriveAll(state);
    d.gateAccountability = deriveGateAccountability(state, d);
    return d;
  }, [state]);

  const value = useMemo(
    () => ({
      state,
      ...api_,
      d: derived,
      today: derived?.sprint.today ?? '',
      status,
      error,
      pending,
      saved: pending === 0 && !error,
      get: (p, f) => getPath(state, p, f),
    }),
    [state, api_, derived, status, error, pending],
  );

  if (status === 'loading' && !state) return <BootScreen state="loading" />;
  if (status === 'error' && !state) return <BootScreen state="error" error={error} onRetry={hydrate} />;

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

function BootScreen({ state, error, onRetry }) {
  return (
    <div className="boot">
      <div className="boot-inner">
        <h1>Bridge45</h1>
        <div className="tag">Zero → Proof → Scale</div>
        {state === 'loading' ? (
          <p className="muted">Loading the sprint from the API…</p>
        ) : (
          <>
            <p className="boot-error">{error?.message ?? 'The API is unreachable.'}</p>
            <p className="muted tiny">
              Start it with <span className="mono">npm run dev:api</span>, or bring the whole stack
              up with <span className="mono">npm run local</span>.
            </p>
            <button className="btn primary" onClick={onRetry}>Retry</button>
          </>
        )}
      </div>
    </div>
  );
}

export function useStore() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useStore must be used inside <StoreProvider>');
  return ctx;
}

// Resolves 'system' | 'light' | 'dark' onto <html data-theme>, and keeps
// following the OS while the preference is 'system'.
// persist:false — apply only (public pages); do not overwrite the saved app preference.
export function useTheme(preference = 'system', { persist = true } = {}) {
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const mq = typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(prefers-color-scheme: dark)')
      : null;

    const apply = () => {
      const dark = preference === 'dark' || (preference === 'system' && !!mq && mq.matches);
      document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
    };

    // The preference itself lives on the server, but the pre-paint script in
    // index.html runs before any fetch can finish — so mirror just this one
    // rendering hint locally to avoid a flash of the wrong theme on load.
    if (persist) {
      try {
        localStorage.setItem('bridge45.theme', preference);
      } catch {
        /* private mode, or storage disabled — the flash is survivable */
      }
    }

    apply();
    if (preference !== 'system' || !mq) return undefined;
    if (mq.addEventListener) {
      mq.addEventListener('change', apply);
      return () => mq.removeEventListener('change', apply);
    }
    mq.addListener(apply);
    return () => mq.removeListener(apply);
  }, [preference, persist]);
}

export { currentDate };
